// Transient baseline of each block's sizing fields, as the strategy was first
// seen this session (keyed by StrategyContext.loadedAt). Slice B made the
// Backtest panel edit & persist sizing into the TSL blocks; this lets any
// window offer "Restore original sizing" so the values edited in the TSL
// builder / saved into the strategy are always recoverable.
//
// In-memory only (NOT persisted) — it is purely a UI restore baseline and must
// never act as a money-math source. The engine/converter always read the live
// blocks; this store is read only when the user clicks Restore.

import type { Block } from '../types';
import { BlockType } from '../types';

// Native sizing field names across strategy_config + entry/signal blocks.
export const SIZING_SNAPSHOT_FIELDS = [
  'default_qty_type', 'default_qty_value',
  'position_sizing_method', 'quantity_type', 'quantity_value',
  'equity_percent', 'risk_per_trade', 'fixed_risk_usd', 'atr_size_multiplier',
  'equity_basis', 'max_notional_usd', 'min_position_size', 'max_position_size',
  'min_contracts', 'max_contracts', 'min_stop_distance', 'point_value', 'mdl_buffer_pct',
] as const;

const store = new Map<number, Record<string, Record<string, unknown>>>();

function pickSizing(data: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const k of SIZING_SNAPSHOT_FIELDS) if (data[k] !== undefined) out[k] = data[k];
  return out;
}

// Capture ONCE per loadedAt (no-clobber) — the first sighting of a freshly
// loaded strategy is the baseline. Subsequent edits never overwrite it.
export function captureOriginalSizing(loadedAt: number | null, blocks: Block[]): void {
  if (loadedAt == null || store.has(loadedAt)) return;
  const byId: Record<string, Record<string, unknown>> = {};
  for (const b of blocks) {
    if (b.type === BlockType.STRATEGY_CONFIG || b.type === BlockType.ENTRY) {
      byId[b.id] = pickSizing((b.data ?? {}) as Record<string, unknown>);
    }
  }
  store.set(loadedAt, byId);
}

export function getOriginalSizing(loadedAt: number | null, blockId: string): Record<string, unknown> | null {
  if (loadedAt == null) return null;
  return store.get(loadedAt)?.[blockId] ?? null;
}

// True when the block's current sizing differs from its captured baseline.
export function sizingChangedFromOriginal(
  loadedAt: number | null,
  blockId: string,
  currentData: Record<string, unknown>,
): boolean {
  const orig = getOriginalSizing(loadedAt, blockId);
  if (!orig) return false;
  return SIZING_SNAPSHOT_FIELDS.some(
    k => JSON.stringify(orig[k] ?? null) !== JSON.stringify(currentData[k] ?? null),
  );
}
